import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Mail, FileText, Image as ImageIcon } from "lucide-react";
import Logo from "../../components/site/Logo";
import Footer from "../../components/site/Footer";
import { SITE, BRIEFS } from "../../data";
import useSeo from "../../hooks/useSeo";

const destacados = BRIEFS.slice(0, 6);

export default function Prensa() {
  useSeo({
    title: "Prensa — España Eficiente (E²)",
    description:
      "Sala de prensa de España Eficiente (E²): contacto para medios, Policy Briefs destacados y kit de prensa descargable (logo y Carta de Responsabilidad).",
    path: "prensa",
  });

  return (
    <div className="bg-[#FAFAFA] min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="prensa-home-link">
            <Logo />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-espana-red hover:text-espana-reddark transition-colors">
            <ArrowLeft size={16} /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
          <div className="h-1.5 w-24 bg-espana-red mb-6" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-3">
            Sala de prensa
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
            Prensa y medios
          </h1>
          <p className="mt-5 text-lg text-gray-600 max-w-3xl leading-relaxed">
            Espacio para periodistas y medios que deseen informar sobre España
            Eficiente (E²), citar nuestros Policy Briefs o solicitar una entrevista.
            Todos los documentos se basan en fuentes oficiales y públicas.
          </p>

          {/* Contacto de prensa */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-200 p-6">
              <Mail className="text-espana-red mb-3" size={24} />
              <h2 className="font-bold text-lg mb-1">Contacto directo</h2>
              <p className="text-sm text-gray-600 mb-3">
                Para entrevistas, declaraciones o ampliación de datos:
              </p>
              <a href={`mailto:${SITE.email}`} data-testid="prensa-email" className="text-espana-red font-bold hover:underline">
                {SITE.email}
              </a>
            </div>
            <div className="bg-white border border-gray-200 p-6">
              <FileText className="text-espana-red mb-3" size={24} />
              <h2 className="font-bold text-lg mb-1">Sobre E² (boilerplate)</h2>
              <p className="text-sm text-gray-600">
                España Eficiente (E²) es una iniciativa independiente de reforma
                política, administrativa y tecnológica. Publica Policy Briefs y
                propuestas basadas en datos y fuentes oficiales, sin vinculación a
                ningún partido. Lema: «Eficiencia, transparencia y prosperidad para
                España».
              </p>
            </div>
          </div>

          {/* Kit de prensa */}
          <h2 className="mt-14 text-2xl font-black tracking-tight">Kit de prensa</h2>
          <p className="mt-2 text-gray-600">Material descargable para uso editorial.</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              href="/favicon-512.png"
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prensa-logo"
              className="flex items-center gap-3 bg-white border border-gray-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform"
            >
              <ImageIcon size={22} className="text-espana-red shrink-0" />
              <div>
                <div className="font-bold">Logotipo E²</div>
                <div className="text-xs text-gray-500">PNG · alta resolución</div>
              </div>
              <Download size={18} className="ml-auto text-espana-red" />
            </a>
            <a
              href={SITE.cartaPdf}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prensa-carta"
              className="flex items-center gap-3 bg-white border border-gray-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform"
            >
              <FileText size={22} className="text-espana-red shrink-0" />
              <div>
                <div className="font-bold">Carta de Responsabilidad</div>
                <div className="text-xs text-gray-500">PDF</div>
              </div>
              <Download size={18} className="ml-auto text-espana-red" />
            </a>
          </div>

          {/* Briefs destacados */}
          <h2 className="mt-14 text-2xl font-black tracking-tight">Policy Briefs destacados</h2>
          <p className="mt-2 text-gray-600">
            Documentos de referencia para citar. Consulta el índice completo en la{" "}
            <Link to="/#briefs" className="text-espana-red font-bold hover:underline">
              portada
            </Link>
            .
          </p>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            {destacados.map((b) => (
              <a
                key={b.id}
                href={b.pdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`prensa-brief-${b.id}`}
                className="bg-white border border-gray-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform"
              >
                <span className="text-xs font-bold text-gray-500">{b.topic}</span>
                <h3 className="font-bold mt-1 leading-snug">{b.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-espana-red">
                  <Download size={15} /> Descargar
                </span>
              </a>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
